// src/components/EnrollmentForm.tsx
import React, { useState } from 'react';
import './EnrollmentForm.css';
import { API_ENDPOINTS } from '../config/api';

interface FormData {
  student_name: string;
  parent_name: string;
  email: string;
  phone: string;
  grade: string;
  message: string;
}

const EnrollmentForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    student_name: '',
    parent_name: '',
    email: '',
    phone: '',
    grade: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(API_ENDPOINTS.ENROLL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: `Admission Inquiry for ${formData.student_name} - Grade ${formData.grade}`
        }),
      });

      if (response.ok) {
        setSubmitStatus({ success: true, message: 'Thank you! We will contact you soon.' });
        setFormData({
          student_name: '',
          parent_name: '',
          email: '',
          phone: '',
          grade: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ success: false, message: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enrollment-overlay">
      <div className="enrollment-form-container">
        <button className="close-button" onClick={onClose}>&times;</button>
        <h2>Admission Inquiry</h2>
        
        {submitStatus ? (
          <div className={`submit-message ${submitStatus.success ? 'success' : 'error'}`}>
            <div className='mb-5'>{submitStatus.message}</div>
            <button onClick={onClose} className="btn-primary">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Student's Full Name*</label>
              <input
                type="text"
                name="student_name"
                value={formData.student_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Parent's/Guardian's Name*</label>
              <input
                type="text"
                name="parent_name"
                value={formData.parent_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone*</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Grade Applying For*</label>
              <select
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                required
              >
                <option value="">Select Grade</option>
                <option value="Play Group">Play Group (2-3 Years)</option>
                <option value="Nursery">Nursery (3-4 Years)</option>
                <option value="LKG">LKG (4-5 Years)</option>
                <option value="UKG">UKG (5-6 Years)</option>
                <option value="1">Class 1</option>
                <option value="2">Class 2</option>
                <option value="3">Class 3</option>
                <option value="4">Class 4</option>
                <option value="5">Class 5</option>
              </select>
            </div>

            <div className="form-group">
              <label>Additional Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Any additional information you'd like to share..."
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default EnrollmentForm;